import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { ridesManagementAPI, handleApiError } from '../../services/api';
import type {
  AdminRide,
  AdminRideBooking,
  AdminRideDetail,
  AdminRideDetailDriver,
  AdminRideListResponse,
  AdminRideStatusFilter,
  RideStatus,
} from '../../types';
import {
  BOOKING_CHIP,
  STATUS_CHIP,
  STATUS_LABELS,
  dayLabel,
  deviceText,
  fullDate,
  preferencesText,
  routeText,
  seatsLeftText,
  shortTime,
  sofiaDateTime,
  sofiaToday,
} from './format';

const STATUS_TABS: AdminRideStatusFilter[] = ['active', 'completed', 'cancelled', 'expired', 'all'];
const PAGE_SIZES = [25, 50, 100];
// The list reloads on its own while the tab is visible, so a new ride shows up.
export const AUTO_REFRESH_MS = 60_000;
const SEARCH_DELAY_MS = 300;

const tabLabel = (status: AdminRideStatusFilter): string => (status === 'all' ? 'Всички' : STATUS_LABELS[status]);

const tabCount = (status: AdminRideStatusFilter, counts?: Record<RideStatus, number>): number | null => {
  if (!counts) return null;
  if (status === 'all') return Object.values(counts).reduce((sum, n) => sum + n, 0);
  return counts[status] ?? 0;
};

const driverName = (ride: AdminRide): string =>
  ride.driver ? `${ride.driver.first_name} ${ride.driver.last_name}`.trim() : 'Изтрит профил';

const SeatMeter: React.FC<{ ride: AdminRide }> = ({ ride }) => {
  const total = Math.max(ride.available_seats, 1);
  const pct = Math.min(100, Math.round((ride.booked_seats / total) * 100));
  return (
    <div className="rl-seats">
      <div className="rl-seats-top">
        <strong>
          {ride.booked_seats}/{ride.available_seats}
        </strong>
        <span className={ride.seats_left === 0 ? 'rl-full' : ''}>{seatsLeftText(ride.seats_left)}</span>
      </div>
      <div className="rl-meter" aria-hidden="true">
        <div className="rl-meter-fill" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

const RideFacts: React.FC<{ ride: AdminRide }> = ({ ride }) => (
  <dl className="rl-detail">
    <div>
      <dt>ID</dt>
      <dd>
        <code>{ride.id}</code>
      </dd>
    </div>
    <div>
      <dt>Тръгване</dt>
      <dd>
        {fullDate(ride.departure_date)}, {shortTime(ride.departure_time)}
      </dd>
    </div>
    <div>
      <dt>През</dt>
      <dd>{ride.via.length ? ride.via.join(', ') : 'Директно'}</dd>
    </div>
    <div>
      <dt>Автомобил</dt>
      <dd>
        {ride.vehicle_info || '-'}
        {ride.registration_number ? ` · ${ride.registration_number}` : ''}
      </dd>
    </div>
    <div>
      <dt>Имейл</dt>
      <dd>{ride.driver ? <a href={`mailto:${ride.driver.email}`}>{ride.driver.email}</a> : '-'}</dd>
    </div>
    <div>
      <dt>Телефон</dt>
      <dd>
        {ride.driver?.phone_number ? <a href={`tel:${ride.driver.phone_number}`}>{ride.driver.phone_number}</a> : '-'}
      </dd>
    </div>
    <div>
      <dt>Тип</dt>
      <dd>{ride.trip_type === 'paid' ? 'Платено' : 'Безплатно'}</dd>
    </div>
    <div>
      <dt>Публикувано</dt>
      <dd>{sofiaDateTime(ride.created_at)}</dd>
    </div>
  </dl>
);

const yesNo = (value: boolean): string => (value ? 'Да' : 'Не');

const DriverFacts: React.FC<{ driver: AdminRideDetailDriver }> = ({ driver }) => (
  <dl className="rl-detail">
    <div>
      <dt>Регистриран</dt>
      <dd>{sofiaDateTime(driver.member_since)}</dd>
    </div>
    <div>
      <dt>Последна активност</dt>
      <dd>{sofiaDateTime(driver.last_active_at)}</dd>
    </div>
    <div>
      <dt>Последен вход</dt>
      <dd>{sofiaDateTime(driver.last_login)}</dd>
    </div>
    <div>
      <dt>Рейтинг</dt>
      <dd>
        {driver.average_rating !== null
          ? `${driver.average_rating.toFixed(1)} (${driver.total_ratings} оценки)`
          : 'Няма оценки'}
      </dd>
    </div>
    <div>
      <dt>Пътувания</dt>
      <dd>
        {driver.trips_as_driver} като шофьор · {driver.trips_as_passenger} като пътник
      </dd>
    </div>
    <div>
      <dt>Потвърден</dt>
      <dd>
        Имейл: {yesNo(driver.is_email_verified)} · Телефон: {yesNo(driver.is_phone_verified)}
      </dd>
    </div>
    <div>
      <dt>Език</dt>
      <dd>{driver.language === 'en' ? 'Английски' : 'Български'}</dd>
    </div>
    <div>
      <dt>Модерация</dt>
      <dd>
        {driver.is_banned ? 'Блокиран' : driver.is_suspended ? 'Суспендиран' : 'Активен'}
        {driver.warning_count > 0 ? ` · ${driver.warning_count} предупреждения` : ''}
      </dd>
    </div>
    <div>
      <dt>Устройства</dt>
      <dd>
        {driver.devices.length === 0
          ? 'Няма устройство с известия'
          : driver.devices.map((device, index) => (
              <div key={index}>
                {deviceText(device)}
                <span className="rl-sub"> · обновено {sofiaDateTime(device.updated_at)}</span>
              </div>
            ))}
      </dd>
    </div>
  </dl>
);

const Bookings: React.FC<{ bookings: AdminRideBooking[] }> = ({ bookings }) => {
  if (bookings.length === 0) return <p className="rl-sub">Няма заявки за това пътуване.</p>;
  return (
    <div className="rl-bookings">
      <table>
        <thead>
          <tr>
            <th>Пътник</th>
            <th>Контакт</th>
            <th>Места</th>
            <th>Статус</th>
            <th>Заявено</th>
            <th>Качване и съобщение</th>
          </tr>
        </thead>
        <tbody>
          {bookings.map((booking) => {
            const chip = BOOKING_CHIP[booking.status] ?? { label: booking.status, tone: 'muted' };
            const person = booking.passenger;
            return (
              <tr key={booking.id}>
                <td>{person ? `${person.first_name} ${person.last_name}`.trim() : 'Изтрит профил'}</td>
                <td>
                  {person?.phone_number && <a href={`tel:${person.phone_number}`}>{person.phone_number}</a>}
                  {person && <div className="rl-sub">{person.email}</div>}
                </td>
                <td>{booking.seats}</td>
                <td>
                  <span className={`rl-chip ${chip.tone}`}>{chip.label}</span>
                </td>
                <td>{sofiaDateTime(booking.created_at)}</td>
                <td>
                  {booking.pickup_address || '-'}
                  {booking.message && <div className="rl-sub">„{booking.message}“</div>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// The open row: the list facts at once, the rest from GET /admin/rides/{id}.
const RideDetail: React.FC<{ ride: AdminRide }> = ({ ride }) => {
  const [detail, setDetail] = useState<AdminRideDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setDetail(null);
    setError(null);
    ridesManagementAPI
      .getRide(ride.id)
      .then((response) => {
        if (!cancelled) setDetail(response.data);
      })
      .catch((err) => {
        if (!cancelled) setError(handleApiError(err));
      });
    return () => {
      cancelled = true;
    };
  }, [ride.id]);

  return (
    <div className="rl-detail-stack">
      <RideFacts ride={ride} />
      {error && <p className="rl-error">Детайлите не се заредиха: {error}</p>}
      {!detail && !error && (
        <p className="rl-sub">
          <i className="fas fa-spinner fa-spin"></i> Зареждане на детайлите...
        </p>
      )}
      {detail && (
        <>
          <dl className="rl-detail">
            <div className="rl-detail-wide">
              <dt>Описание</dt>
              <dd>{detail.description || '-'}</dd>
            </div>
            <div>
              <dt>Условия</dt>
              <dd>{preferencesText(detail)}</dd>
            </div>
            <div>
              <dt>Маршрут</dt>
              <dd>{routeText(detail.distance_km, detail.duration_minutes)}</dd>
            </div>
            <div>
              <dt>Региони</dt>
              <dd>
                {detail.origin_region || '-'} → {detail.destination_region || '-'}
              </dd>
            </div>
            <div>
              <dt>Фестивал</dt>
              <dd>{detail.festival?.name || '-'}</dd>
            </div>
            <div>
              <dt>Последна промяна</dt>
              <dd>{sofiaDateTime(detail.updated_at)}</dd>
            </div>
          </dl>
          {detail.driver && (
            <section>
              <h4 className="rl-detail-title">Шофьор</h4>
              <DriverFacts driver={detail.driver} />
            </section>
          )}
          <section>
            <h4 className="rl-detail-title">Заявки ({detail.bookings.length})</h4>
            <Bookings bookings={detail.bookings} />
          </section>
        </>
      )}
    </div>
  );
};

const RideList: React.FC = () => {
  const [status, setStatus] = useState<AdminRideStatusFilter>('active');
  const [search, setSearch] = useState<string>('');
  const [query, setQuery] = useState<string>('');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [page, setPage] = useState<number>(1);
  const [size, setSize] = useState<number>(25);
  const [data, setData] = useState<AdminRideListResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [loadedAt, setLoadedAt] = useState<Date | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const request = useRef(0);

  // Typing waits a moment before it asks the backend.
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setQuery(search.trim());
      setPage(1);
    }, SEARCH_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [search]);

  const load = useCallback(
    async (quiet = false): Promise<void> => {
      const id = ++request.current;
      if (!quiet) setLoading(true);
      try {
        const response = await ridesManagementAPI.listRides({
          status,
          q: query || undefined,
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
          page,
          size,
        });
        // An older answer that lands late must not replace a newer one.
        if (id !== request.current) return;
        setData(response.data);
        setError(null);
        setLoadedAt(new Date());
      } catch (err) {
        if (id === request.current) setError(handleApiError(err));
      } finally {
        if (id === request.current) setLoading(false);
      }
    },
    [status, query, dateFrom, dateTo, page, size],
  );

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') load(true);
    }, AUTO_REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [load]);

  const changeStatus = (next: AdminRideStatusFilter): void => {
    setStatus(next);
    setPage(1);
    setOpenId(null);
  };

  const clearFilters = (): void => {
    setSearch('');
    setQuery('');
    setDateFrom('');
    setDateTo('');
    setPage(1);
  };

  const today = sofiaToday();
  const total = data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / size));
  const first = total === 0 ? 0 : (page - 1) * size + 1;
  const last = Math.min(total, page * size);
  const filtered = Boolean(query || dateFrom || dateTo);

  return (
    <div className="rl">
      <div className="rl-tabs" role="tablist" aria-label="Статус">
        {STATUS_TABS.map((tab) => {
          const count = tabCount(tab, data?.counts);
          return (
            <button
              key={tab}
              type="button"
              role="tab"
              aria-selected={status === tab}
              className={`rl-tab ${status === tab ? 'active' : ''}`}
              onClick={() => changeStatus(tab)}
            >
              {tabLabel(tab)}
              {count !== null && <span className="rl-tab-count">{count}</span>}
            </button>
          );
        })}
      </div>

      <div className="rl-card">
        <div className="rl-toolbar">
          <label className="rl-search">
            <i className="fas fa-search" aria-hidden="true"></i>
            <input
              type="search"
              className="bo-input"
              placeholder="Град, шофьор, имейл, телефон или № на пътуване"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Търсене"
            />
          </label>
          <label className="rl-date">
            <span>От</span>
            <input
              type="date"
              className="bo-input"
              value={dateFrom}
              onChange={(e) => {
                setDateFrom(e.target.value);
                setPage(1);
              }}
            />
          </label>
          <label className="rl-date">
            <span>До</span>
            <input
              type="date"
              className="bo-input"
              value={dateTo}
              onChange={(e) => {
                setDateTo(e.target.value);
                setPage(1);
              }}
            />
          </label>
          {filtered && (
            <button type="button" className="btn btn-secondary btn-sm" onClick={clearFilters}>
              <i className="fas fa-times"></i> Изчисти
            </button>
          )}
          <div className="rl-toolbar-end">
            {loadedAt && (
              <span className="rl-updated">
                Обновено {loadedAt.toLocaleTimeString('bg-BG', { hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => load()}
              disabled={loading}
              aria-label="Обнови"
            >
              <i className={`fas fa-sync-alt ${loading ? 'fa-spin' : ''}`}></i>
            </button>
          </div>
        </div>

        {error && (
          <div className="rl-error" role="alert">
            <i className="fas fa-exclamation-triangle"></i> {error}
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => load()}>
              Опитай пак
            </button>
          </div>
        )}

        <div className="rl-table-wrap">
          <table className="rl-table">
            <thead>
              <tr>
                <th>№</th>
                <th>Маршрут</th>
                <th>Тръгване</th>
                <th>Шофьор</th>
                <th>Места</th>
                <th>Заявки</th>
                <th>Статус</th>
                <th aria-label="Детайли" />
              </tr>
            </thead>
            <tbody>
              {data?.items.map((ride) => {
                const open = openId === ride.id;
                const chip = STATUS_CHIP[ride.status];
                return (
                  <Fragment key={ride.id}>
                    <tr
                      className={`rl-row ${open ? 'open' : ''}`}
                      onClick={() => setOpenId(open ? null : ride.id)}
                    >
                      <td className="rl-num">#{ride.ride_number}</td>
                      <td className="rl-c-route">
                        <div className="rl-route">
                          {ride.origin} <i className="fas fa-arrow-right" aria-hidden="true"></i> {ride.destination}
                        </div>
                        {ride.via.length > 0 && <div className="rl-sub">през {ride.via.join(', ')}</div>}
                      </td>
                      <td className="rl-c-when">
                        <div className="rl-when" title={fullDate(ride.departure_date)}>
                          <span className={ride.departure_date === today ? 'rl-today' : ''}>
                            {dayLabel(ride.departure_date, today)}
                          </span>{' '}
                          {shortTime(ride.departure_time)}
                        </div>
                        {ride.status === 'active' && ride.departed && <span className="rl-chip warning">Тръгнало</span>}
                      </td>
                      <td className="rl-c-driver">
                        <div className="rl-driver">
                          {driverName(ride)}
                          {ride.driver?.is_banned && <span className="rl-chip error">Блокиран</span>}
                        </div>
                        <div className="rl-sub">{ride.driver?.phone_number || ride.driver?.email || ''}</div>
                      </td>
                      <td className="rl-c-seats">
                        <SeatMeter ride={ride} />
                      </td>
                      <td className="rl-c-requests">
                        {ride.pending_bookings > 0 ? (
                          <span className="rl-chip warning" title="Чакат одобрение от шофьора">
                            {ride.pending_bookings === 1 ? '1 чакаща' : `${ride.pending_bookings} чакащи`}
                          </span>
                        ) : (
                          <span className="rl-sub">-</span>
                        )}
                      </td>
                      <td className="rl-c-status">
                        <span className={`rl-chip ${chip.tone}`}>{chip.label}</span>
                      </td>
                      <td className="rl-caret">
                        <button
                          type="button"
                          className="rl-expand"
                          aria-expanded={open}
                          aria-label={open ? 'Скрий детайлите' : 'Покажи детайлите'}
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenId(open ? null : ride.id);
                          }}
                        >
                          <i className={`fas fa-chevron-${open ? 'up' : 'down'}`}></i>
                        </button>
                      </td>
                    </tr>
                    {open && (
                      <tr className="rl-detail-row">
                        <td colSpan={8}>
                          <RideDetail ride={ride} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>

          {loading && !data && (
            <div className="rl-empty">
              <i className="fas fa-spinner fa-spin"></i> Зареждане...
            </div>
          )}
          {data && data.items.length === 0 && (
            <div className="rl-empty">
              <i className="fas fa-car-side"></i>
              <p>{filtered ? 'Няма пътувания по този филтър.' : `Няма ${tabLabel(status).toLowerCase()} пътувания.`}</p>
            </div>
          )}
        </div>

        <div className="rl-footer">
          <span>
            {first}-{last} от {total}
          </span>
          <label className="rl-size">
            На страница
            <select
              className="bo-input"
              value={size}
              onChange={(e) => {
                setSize(Number(e.target.value));
                setPage(1);
              }}
            >
              {PAGE_SIZES.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
          <div className="rl-pager">
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              disabled={page <= 1 || loading}
              onClick={() => setPage(page - 1)}
              aria-label="Предишна страница"
            >
              <i className="fas fa-chevron-left"></i>
            </button>
            <span>
              {page} / {pages}
            </span>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              disabled={page >= pages || loading}
              onClick={() => setPage(page + 1)}
              aria-label="Следваща страница"
            >
              <i className="fas fa-chevron-right"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RideList;
